import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Lock } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { formatDate } from '@/lib/dateFormat';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { EmailBuilder } from '@/components/email/EmailBuilder';
import { TemplateGallery } from '@/components/email/TemplateGallery';
import { type EmailBlock, type GalleryTemplate, createBlock } from '@/lib/emailBlocks';

// A template row can come from three places — see backend
// EmailBusinessService.getTemplates() for the merge logic:
//   'system'     → super-admin default, read-only until cloned
//   'customized' → tenant's clone of a system template
//   'own'        → tenant's own template
type TemplateSource = 'system' | 'customized' | 'own';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  blocks: EmailBlock[];
  category?: string;
  createdAt: string;
  source?: TemplateSource;
}

export function EmailTemplatesPage() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [cloneConfirmFor, setCloneConfirmFor] = useState<EmailTemplate | null>(null);
  const [showGallery, setShowGallery] = useState(false);
  const [builderState, setBuilderState] = useState<{
    isOpen: boolean;
    editId?: string;
    blocks: EmailBlock[];
    name: string;
    subject: string;
  } | null>(null);

  const load = () => {
    setLoading(true);
    api.get('/email-templates').then(r => setTemplates(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/email-templates/${deleteId}`);
      toast('success', 'Template deleted');
      setDeleteId(null);
      load();
    } catch { toast('error', 'Failed to delete'); }
  };

  const openBuilder = (blocks: EmailBlock[], name = '', subject = '', editId?: string) => {
    setBuilderState({ isOpen: true, editId, blocks, name, subject });
    setShowGallery(false);
  };

  const handleGallerySelect = (t: GalleryTemplate) => {
    openBuilder(t.blocks.map(b => ({ ...b, id: `b_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` })), t.name, t.subject);
  };

  const handleBlankTemplate = () => {
    openBuilder([createBlock('header'), createBlock('text'), createBlock('button'), createBlock('footer')], '', '');
  };

  const handleSaveBuilder = async (data: { name: string; subject: string; blocks: EmailBlock[] }) => {
    try {
      const payload = { name: data.name, subject: data.subject, htmlBody: '', blocks: data.blocks };
      if (builderState?.editId) {
        await api.patch(`/email-templates/${builderState.editId}`, payload);
        toast('success', 'Template updated');
      } else {
        await api.post('/email-templates', payload);
        toast('success', 'Template created');
      }
      setBuilderState(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save template');
    }
  };

  const handleEditTemplate = (t: EmailTemplate) => {
    // System (super-admin default) templates are read-only from the vendor
    // side. Editing triggers a copy-on-write clone into the tenant's own
    // EmailTemplate table; subsequent edits are isolated per tenant.
    if (t.source === 'system') {
      setCloneConfirmFor(t);
      return;
    }
    const blocks = (Array.isArray(t.blocks) && t.blocks.length > 0)
      ? t.blocks
      : [createBlock('header'), createBlock('text')]; // fallback for old HTML-only templates
    openBuilder(blocks, t.name, t.subject, t.id);
  };

  const confirmCloneAndEdit = async () => {
    if (!cloneConfirmFor) return;
    const sys = cloneConfirmFor;
    setCloneConfirmFor(null);
    try {
      const { data: clone } = await api.post(`/email-templates/clone/${sys.id}`);
      toast('success', 'Default template copied — edit your copy');
      const blocks = (Array.isArray(clone.blocks) && clone.blocks.length > 0)
        ? clone.blocks
        : (Array.isArray(sys.blocks) && sys.blocks.length > 0
            ? sys.blocks
            : [createBlock('header'), createBlock('text')]);
      openBuilder(blocks, clone.name, clone.subject, clone.id);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to clone template');
    }
  };

  // ── If builder is open, show full-screen builder ────────────────────────
  if (builderState?.isOpen) {
    return (
      <EmailBuilder
        initialBlocks={builderState.blocks}
        initialName={builderState.name}
        initialSubject={builderState.subject}
        onSave={handleSaveBuilder}
        onBack={() => setBuilderState(null)}
      />
    );
  }

  return (
    <DashboardLayout title="Email Templates">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#6366F1]">Email Templates</h2>
            <p className="text-sm text-[#64748B] mt-1">{templates.length} templates</p>
          </div>
          <button
            onClick={() => setShowGallery(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg"
          >
            <Plus className="h-4 w-4" /> New Template
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400 mb-3">No email templates yet</p>
            <button onClick={() => setShowGallery(true)}
              className="px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg">
              Create Your First Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => {
              const firstHeader = (Array.isArray(t.blocks) ? t.blocks : []).find((b: any) => b.type === 'header');
              const headerColor = firstHeader?.props?.bgColor || '#6366F1';
              const isSystem = t.source === 'system';
              const isCustomized = t.source === 'customized';
              return (
                <div key={t.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group">
                  {/* Color preview */}
                  <div className="h-16 flex items-center justify-center px-4 relative" style={{ backgroundColor: headerColor }}>
                    <span className="text-white text-sm font-bold truncate">
                      {firstHeader?.props?.text || t.name}
                    </span>
                    {isSystem && (
                      <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold bg-white/90 text-slate-700 px-1.5 py-0.5 rounded">
                        <Lock className="h-3 w-3" /> Default
                      </span>
                    )}
                    {isCustomized && (
                      <span className="absolute top-2 right-2 text-[10px] font-semibold bg-emerald-500/90 text-white px-1.5 py-0.5 rounded">
                        Customized
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-slate-800 truncate">{t.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{t.subject}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] text-slate-400">
                        {isSystem ? 'Platform default' : formatDate(t.createdAt)}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleEditTemplate(t)}
                          title={isSystem ? 'Edit — creates your own copy' : 'Edit'}
                          className="text-slate-400 hover:text-[#6366F1]"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {!isSystem && (
                          <button onClick={() => setDeleteId(t.id)} className="text-slate-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Template Gallery */}
      {showGallery && (
        <TemplateGallery
          onSelect={handleGallerySelect}
          onBlank={handleBlankTemplate}
          onClose={() => setShowGallery(false)}
        />
      )}

      <ConfirmModal isOpen={!!deleteId} title="Delete Template?" message="This action cannot be undone."
        onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />

      <ConfirmModal
        isOpen={!!cloneConfirmFor}
        title="Customize default template?"
        message="This is a platform-default template. Editing it will create your own copy — the original stays untouched and your changes are saved only for your organization."
        confirmText="Create my copy"
        onConfirm={confirmCloneAndEdit}
        onCancel={() => setCloneConfirmFor(null)}
      />
    </DashboardLayout>
  );
}
